import { AppDispatch } from ".";
import { Building, Coordinates } from "../models/data";
import {
  setNearbyBuildings,
  setUserLocation,
  setSearchResults,
  addBuilding,
  setFollowedBuildings,
  followBuilding as followBuildingAction,
  unfollowBuilding as unfollowBuildingAction,
  setSearchPredictions,
  setSessionToken,
  clearSessionToken,
} from "./buildings";
import {
  generatePlusCode,
  generateDisplayAddress,
  isPlusCodeQuery,
  normalizePlusCode,
  validatePlusCode,
  getPlusCodeCenter,
} from "../utils/plusCode";
import { v4 as uuidv4 } from 'uuid';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/api';

export const getUserLocation = () => {
  return async (dispatch: AppDispatch) => {
    try {
      if (!navigator.geolocation) {
        throw new Error("Geolocation is not supported by your browser");
      }

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        }
      );

      const coordinates: Coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      dispatch(setUserLocation(coordinates));
      return coordinates;
    } catch (error) {
      console.error("Error getting location:", error);
      throw error;
    }
  };
};

export const fetchNearbyBuildings = (location: Coordinates) => {
  return async (dispatch: AppDispatch) => {
    try {
      // Fetch nearby buildings from mock server
      const response = await fetch(
        `${API_ENDPOINTS.buildings.nearby}?` +
        `lat=${location.latitude}&` +
        `lng=${location.longitude}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch nearby buildings");
      }

      const data = await response.json();
      const buildings = data.buildings;

      dispatch(setNearbyBuildings(buildings));
      return buildings;
    } catch (error) {
      console.error("Error fetching nearby buildings:", error);
      throw error;
    }
  };
};

// Helper function to calculate distance (Haversine formula)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Radius of Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Search buildings by Plus Code
export const searchBuildingsByPlusCode = ({ query }: { query: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      const normalizedCode = normalizePlusCode(query);

      if (!validatePlusCode(normalizedCode)) {
        throw new Error("Invalid Plus Code format. Example: 849VCWC8+R9");
      }

      // Call backend Plus Code search endpoint
      const response = await fetch(
        API_ENDPOINTS.search.plusCode,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ plusCode: normalizedCode }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to search Plus Code");
      }

      const building = await response.json();
      const buildings: Building[] = [{
        ...building,
        created: new Date().toISOString(),
        averageRating: 0,
        totalReviews: 0,
      } as Building];

      dispatch(setSearchResults(buildings));
      return buildings;
    } catch (error) {
      console.error("Error searching by Plus Code:", error);
      throw error;
    }
  };
};

export const searchBuildings = ({ query, country }: { query: string; country?: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      // Check if query is a Plus Code
      if (isPlusCodeQuery(query)) {
        return dispatch(searchBuildingsByPlusCode({ query }));
      }

      // Generate or reuse session token
      const sessionToken = uuidv4();
      dispatch(setSessionToken(sessionToken));

      // Call Google Autocomplete API via backend
      const response = await fetch(
        API_ENDPOINTS.search.autocomplete,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query, sessionToken, country }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to search addresses");
      }

      const data = await response.json();

      // Store predictions (not full buildings)
      dispatch(setSearchPredictions(data.predictions));

      return data.predictions;
    } catch (error) {
      console.error("Error searching buildings:", error);
      throw error;
    }
  };
};

// Fetch full place details for a selected prediction
export const fetchPlaceDetails = (placeId: string, sessionToken: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(
        API_ENDPOINTS.search.placeDetails,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ placeId, sessionToken }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to get place details");
      }

      const building = await response.json();

      // Clear session token after use (session complete)
      dispatch(clearSessionToken());

      return building;
    } catch (error) {
      console.error("Error fetching place details:", error);
      dispatch(clearSessionToken());
      throw error;
    }
  };
};

export const createBuilding = ({ building }: { building: Partial<Building> }) => {
  return async (dispatch: AppDispatch) => {
    try {
      // Prepare building data
      const buildingData = {
        ...building,
        averageRating: 0,
        totalReviews: 0,
      };

      const response = await fetch(
        API_ENDPOINTS.buildings.create,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ building: buildingData }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create building");
      }

      const data = await response.json();
      dispatch(addBuilding(data.building));
      return data.building;
    } catch (error) {
      console.error("Error creating building:", error);
      throw error;
    }
  };
};

export const fetchFollowedBuildings = (userId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(
        API_ENDPOINTS.buildings.followed(userId)
      );

      if (!response.ok) {
        throw new Error("Failed to fetch followed buildings");
      }

      const data = await response.json();
      dispatch(setFollowedBuildings(data.buildings));
      return data.buildings;
    } catch (error) {
      console.error("Error fetching followed buildings:", error);
      throw error;
    }
  };
};

export const followBuilding = (buildingId: string, userId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      console.log('[followBuilding] Starting follow request for building:', buildingId);
      const response = await authenticatedFetch(
        API_ENDPOINTS.buildings.follow(buildingId),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log('[followBuilding] Response status:', response.status);

      if (!response.ok) {
        const error = await response.json();
        console.error('[followBuilding] Error response:', error);
        throw new Error(error.error || "Failed to follow building");
      }

      // Fetch the building details and dispatch to store
      console.log('[followBuilding] Fetching updated building details...');
      const buildingResponse = await fetch(
        API_ENDPOINTS.buildings.getById(buildingId, userId)
      );

      if (buildingResponse.ok) {
        const buildingData = await buildingResponse.json();
        console.log('[followBuilding] Dispatching follow action with building:', buildingData.building);
        dispatch(followBuildingAction(buildingData.building));
      }

      // Refresh the canonical followed list so the inferred-parent entries
      // stay in sync (e.g. following a subpremise should also surface its
      // parent; following a parent shouldn't change anything other than its
      // own isFollowing flag).
      try { await dispatch<any>(fetchFollowedBuildings(userId)); } catch { /* non-fatal */ }

      console.log('[followBuilding] Follow completed successfully');
      return true;
    } catch (error) {
      console.error("Error following building:", error);
      throw error;
    }
  };
};

export const unfollowBuilding = (buildingId: string, userId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      console.log('[unfollowBuilding] Starting unfollow request for building:', buildingId);
      const response = await authenticatedFetch(
        API_ENDPOINTS.buildings.follow(buildingId),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log('[unfollowBuilding] Response status:', response.status);

      if (!response.ok) {
        const error = await response.json();
        console.error('[unfollowBuilding] Error response:', error);
        throw new Error(error.error || "Failed to unfollow building");
      }

      console.log('[unfollowBuilding] Dispatching unfollow action');
      dispatch(unfollowBuildingAction(buildingId));
      // Refresh the canonical followed list so inferred parents are removed
      // when their last followed subpremise goes (or stay if another sub
      // keeps the parent inferred).
      try { await dispatch<any>(fetchFollowedBuildings(userId)); } catch { /* non-fatal */ }
      console.log('[unfollowBuilding] Unfollow completed successfully');
      return true;
    } catch (error) {
      console.error("Error unfollowing building:", error);
      throw error;
    }
  };
};
